import { Component, computed, OnInit, signal, inject, WritableSignal, effect } from "@angular/core";
import { CommonModule } from "@angular/common";
import ScheduleDetailHeader from "../../../../domains/jobs/components/shared/schedule-detail-header/schedule-detail-header.component";
import { UiToastService } from "../../../../shared/components/toast/ui-toast.service";
import { ActivatedRoute, Router } from "@angular/router";
import { AbstractControl, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from "@angular/forms";
import { Media, AssertionIssue, SyncableStatus, ImageType } from "../../../../core/database/db";
import { ConfirmationService } from "primeng/api";
import { ConfirmDialog } from "primeng/confirmdialog";
import { v4 as uuidv4, NIL as NIL_UUID } from "uuid";
import { Gallery } from "ng-gallery";
import { Dialog, DialogModule } from "@angular/cdk/dialog";
import { ImagesModalComponent } from "../../../../domains/media/components/images-modal/images-modal.component";
import { BqaImageItem } from "../../../../domains/media/components/images-modal/bqa-image-item";
import { SkeletonModule } from "primeng/skeleton";
import { BlobService } from "../../../../shared/services/blob.service";
import { LoggerService } from "../../../../shared/services/logger.service";
import { ImageCompressionPipelineService } from "../../../../domains/media/services/image-compression-pipeline.service";
import { insertAt } from "../../../../domains/media/utils/media-order.util";
import { CONSTANTS } from "../../../../constants";
import { BqInspectionDataService } from "../../services/bq-inspection-data.service";
import { AssertionIssuesService } from "../../../assertionIssues/services/assertion-issues.service";
import { BqStepDataServiceFactory } from "../../../../personas/shared/bq-step-data-service/bq-step-data-service-factory";
import { DummyBqInspectionDataService } from "../../services/dummy-bq-inspection-data-service";
import { FormFieldComponent } from "../../../../shared/components/form-field/form-field.component";
import { PhotoCaptureComponent } from "../../../../shared/components/photo-capture/photo-capture.component";
import { TextareaComponent } from "../../../../shared/components/textarea/textarea.component";
import { SelectFieldComponent } from "../../../../shared/components/select-field/select-field.component";
import { SeverityInfoComponent } from "../../../../shared/components/severity-info/severity-info.component";
import { HorizontalButton, HorizontalButtonGroupComponent } from "../../../../shared/components/horizontal-button-group/horizontal-button-group.component";
import { VerticalButton, VerticalButtonGroupComponent } from "../../../../shared/components/vertical-button-group/vertical-button-group.component";
import { DatePicker } from "primeng/datepicker";
import { ModalIcon, UiModalService } from "../../../../shared/components/modal/ui-modal.service";
import { DataUpdatedFadeDirective } from "../../../../shared/directives/data-updated-fade.directive";
import { ASSIGN_TO_ME_OPTION_LABEL, SELF_ASSIGNED_VENDOR, withAssignToMeOption } from "../../../vendors/models/VendorDTO";


const severityLevels = [
  { label: "Minor", value: "minor" },
  { label: "Major", value: "major" },
  { label: "Missing", value: "missing" }
];

const unsetSeverity = "unset";

interface NeedsAttentionForm {
  notes: FormControl<string>;
  severity: FormControl<string>;
  photos: FormControl<WritableSignal<Media[]>>;
  externalVendorId?: FormControl<number | null>;
  dueDate?: FormControl<Date | null>;
}

@Component({
  selector: "app-job-needs-attention",
  imports: [CommonModule,
    ConfirmDialog,
    ScheduleDetailHeader,
    ReactiveFormsModule,
    DialogModule,
    SkeletonModule,
    FormFieldComponent,
    PhotoCaptureComponent,
    TextareaComponent,
    SelectFieldComponent,
    SeverityInfoComponent,
    HorizontalButtonGroupComponent,
    VerticalButtonGroupComponent,
    DatePicker,
    DataUpdatedFadeDirective
  ],
  templateUrl: "./job-needs-attention.component.html",
  styleUrl: "./job-needs-attention.component.scss",
  providers: [ConfirmationService, BqStepDataServiceFactory]
})
export class JobNeedsAttentionComponent implements OnInit {
  constructor() {
    effect(() => {
      const existingAssignment = this.bqStepDataService().getVendorAssignmentForIssue(this.issue());
      const preselectedVendorId = existingAssignment
        ? (this.vendorOptions().find(v => v.id === existingAssignment.externalVendorId)?.id ?? null)
        : null;
      this.form.controls.externalVendorId?.setValue(preselectedVendorId);
    });
  }

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private toastService = inject(UiToastService);
  public confirmationService = inject(ConfirmationService);
  private readonly modalService = inject(UiModalService);
  private gallery = inject(Gallery);
  private blobService = inject(BlobService);
  private logger = inject(LoggerService);
  public imageCompressionPipelineService = inject(ImageCompressionPipelineService);
  public bqStepDataServiceFactory = inject(BqStepDataServiceFactory);
  private assertionIssuesService = inject(AssertionIssuesService);
  private formBuilder = inject(FormBuilder);
  dialog = inject(Dialog);

  form: FormGroup<NeedsAttentionForm> = this.formBuilder.nonNullable.group({
    notes: [""],
    severity: [""],
    photos: signal<Media[]>([]),
    externalVendorId: [null],
    dueDate: [null]
  }) as FormGroup<NeedsAttentionForm>;

  severityLevels = severityLevels;
  maxNoteLength = CONSTANTS.CONSTRAINTS.MAX_NOTE_LENGTH;
  maxPhotos = CONSTANTS.CONSTRAINTS.MAX_PHOTOS;

  isSaving = signal(false);
  savingMode = signal<"return" | "addAnother" | null>(null);
  isDeletingIssue = signal(false);
  isRemovingLooksGoodStatus = signal(false);
  isIssue = signal<boolean>(false);
  isExistingIssue = signal<boolean>(false);
  formSubmitted = signal(false);
  isCalendarOpen = signal(false);
  backdropTimeout = signal<ReturnType<typeof setTimeout> | null>(null);

  areaId = signal<number>(0);
  componentId = signal<number>(0);
  assertionId = signal<number>(0);
  issueId = signal<number>(0);

  allIssuesRelatedToAssertion = signal<AssertionIssue[]>([]);
  issue = signal<AssertionIssue>({} as AssertionIssue);
  dataLoaded = signal(false);
  originatingFilter = signal<string | null>(null);

  private activeFilter = signal<"all" | "pending" | "reviewed" | undefined>(undefined);
  private from = signal<string | null>(null);
  private bqStepDataService = signal<BqInspectionDataService>(new DummyBqInspectionDataService(this.toastService));

  galleryItems = computed(() =>
    this.form.controls.photos.value()
      .filter(img => !img.deleted && (img.base64Image || img.resourceUri))
      .map(image => {
        const src = image.base64Image
          ? URL.createObjectURL(this.blobService.base64ToBlob(image.base64Image))
          : (image.resourceUri as string);
        return new BqaImageItem({
          azureBlobUri: src,
          mediaIdentifier: image.fileIdentifier,
          isThumbnailOnly: image.isThumbnailOnly || false
        });
      }));

  photoThumbnails = computed(() => this.galleryItems().map(item => item.imageData.azureBlobUri));

  job = computed(() => this.bqStepDataService()?.job() ?? (() => {
    throw new Error("Job data not available in BqInspectionDataService");
  })());
  components = computed(() => this.bqStepDataService().components() ?? []);
  areas = computed(() => this.bqStepDataService().areas() ?? []);
  assertions = computed(() => this.bqStepDataService().assertions() ?? []);
  issues = computed(() => this.bqStepDataService().assertionIssues() ?? []);
  media = computed(() => this.bqStepDataService().media() ?? []);

  jobId = computed(() => this.job().id);

  area = computed(() => this.areas().find(area => area.id === this.areaId()) ?? (() => {
    throw new Error("Area not found");
  })());
  component = computed(() => this.components().find(component => component.id === this.componentId()) ?? (() => {
    throw new Error("Component not found");
  })());
  assertion = computed(() => this.assertions().find(assertion => assertion.id === this.assertionId()) ?? (() => {
    throw new Error("Assertion not found");
  })());
  isCcmInspection = computed(() => this.bqStepDataService()?.isCcmInspection() ?? false);
  isRefreshingInBackground = computed(() => this.bqStepDataService()?.isRefreshingInBackground() ?? false);
  dataUpdatedPulse = computed(() => this.bqStepDataService()?.dataUpdatedPulse() ?? false);
  vendors = computed(() => this.bqStepDataService()?.vendors() ?? []);
  /** Vendor select options with "Assign to Me" pinned above the trade partners. */
  vendorOptions = computed(() => withAssignToMeOption(this.vendors()));
  protected readonly selfAssignedVendorId = SELF_ASSIGNED_VENDOR.id;
  protected readonly assignToMeLabel = ASSIGN_TO_ME_OPTION_LABEL;

  itemIdentifier = computed(() => (
    this.isIssue()
      ? this.issue().identifier ?? NIL_UUID
      : this.assertion().identifier ?? NIL_UUID
  ));

  isLooksGoodItem = computed(() => !!this.isIssue() === false);
  assertionText = computed(() => this.assertion()?.text ?? "");
  title = computed(() => (this.isIssue() ? "Needs Attention" : "Looks Good"));
  subtitle = computed(() => `${this.area().name} > ${this.component().name}`);
  isLastIssueOnAssertion = computed(() => this.allIssuesRelatedToAssertion().length === 1);
  isLooksGoodAndComplete = computed(() =>
    this.isLooksGoodItem() &&
    (
      (
        this.isCcmInspection()
          ? this.assertion()?.isCcmComplete
          : this.assertion()?.isComplete
      ) ?? false
    )
  );

  isLockedAcceptedCarryover = computed(() =>
    this.isExistingIssue()
    && this.isIssue()
    && this.isCcmInspection()
    && this.assertionIssuesService.isAcceptedCarryoverIssue(this.issue())
  );

  async ngOnInit(): Promise<void> {
    this.areaId.set(parseInt(this.route.snapshot.paramMap.get("areaId") ?? "0"));
    this.componentId.set(parseInt(this.route.snapshot.paramMap.get("componentId") ?? "0"));
    this.assertionId.set(parseInt(this.route.snapshot.paramMap.get("assertionId") ?? "0"));
    this.issueId.set(parseInt(this.route.snapshot.paramMap.get("issueId") ?? "0"));

    const bqStep = this.route.snapshot.parent?.data["bqStep"] as string;
    this.bqStepDataService.set(this.bqStepDataServiceFactory.getService<BqInspectionDataService>(bqStep));

    this.from.set(this.route.snapshot.queryParamMap.get("from"));
    this.originatingFilter.set(this.route.snapshot.queryParamMap.get("filter"));
    this.activeFilter.set(this.route.snapshot.queryParamMap.get("filter") as "all" | "pending" | "reviewed" ?? undefined);
    this.isIssue.set(this.route.snapshot.data["isIssue"]);
    this.isExistingIssue.set(!!this.issueId());

    if (this.isExistingIssue()) {
      const issue = this.issues().find(i => i.id === this.issueId());
      if (!issue) {
        this.toastService.error("Unable to locate the issue.");
        this.goBack();
        return;
      }

      this.issue.set(issue);
    }

    if (this.isIssue()) {
      this.form.controls.notes.setValidators([Validators.required, Validators.maxLength(this.maxNoteLength)]);
      this.form.controls.photos.setValidators(this.hasMediaValidator.bind(this));
      if (this.isCcmInspection()) {
        this.form.controls.severity.setValidators(Validators.required);
      }
    } else {
      this.form.controls.notes.setValidators(Validators.maxLength(this.maxNoteLength));
    }

    const looksGoodImageType = this.isCcmInspection() ? ImageType.LooksGoodCustomerCareManager : ImageType.LooksGoodConstructionManager;
    const imageType = this.isIssue() ? ImageType.Issue : looksGoodImageType;
    const mediaForItem = this.media().filter(m => m.imageType === imageType && m.parentIdentifier === this.itemIdentifier() && !m.deleted);

    if (this.isExistingIssue()) {
      this.form.reset({
        notes: this.issue().text,
        severity: this.issue().severity ?? "",
        photos: signal(mediaForItem),
        dueDate: this.issue().dueDate ?? null
      });
    } else if (this.isLooksGoodItem()) {
      const effectiveAssertionNote = this.isCcmInspection() ? this.assertion().ccmNote : this.assertion().note;
      this.form.reset({
        notes: effectiveAssertionNote ?? "",
        severity: "",
        photos: signal(mediaForItem)
      });
    }
    this.form.updateValueAndValidity();

    this.configureLockedCarryoverFormIfNeeded();

    this.allIssuesRelatedToAssertion.set(this.issues().filter(i => i.assertionIdentifier === this.assertion().identifier));
    this.gallery.ref().load(this.galleryItems());

    this.dataLoaded.set(true);
  }

  openImagesModal(index: number) {
    const isLocked = this.isLockedAcceptedCarryover();
    this.dialog.open<string>(ImagesModalComponent, {
      data: {
        images: this.galleryItems(),
        index,
        isIssue: this.isIssue(),
        isComplete: isLocked,
        ...(isLocked ? {} : {
          onDeleted: async (fileIdentifier: string) => await this.handleImageDeleted(fileIdentifier),
          onAnnotated: (fileIdentifier: string, file: File) => this.handleImageAnnotated(fileIdentifier, file)
        }),
        backdropClass: "images-modal-backdrop"
      }
    });
  }

  async handleImageAnnotated(originalFileIdentifier: string, annotatedFile: File): Promise<string | undefined> {
    // A saved original stays in the list soft-deleted (hidden), so inserting at its index keeps the slot.
    const index = this.form.controls.photos.value().findIndex(photo => photo.fileIdentifier === originalFileIdentifier);
    await this.handleImageDeleted(originalFileIdentifier);
    const [added] = await this.onPhotosSelected([annotatedFile], index);
    return added?.fileIdentifier;
  }

  async handleImageDeleted(fileIdentifier: string): Promise<void> {
    if (this.isLockedAcceptedCarryover()) {
      return;
    }

    try {
      const photos = this.form.controls.photos.value();

      const mediaToBeDeleted = photos.find(photo => photo.fileIdentifier === fileIdentifier);
      const isExistingMedia = !!mediaToBeDeleted && !!mediaToBeDeleted.id;
      if (isExistingMedia) {
        this.form.controls.photos.value.update(currentMedia => currentMedia.map(photo => (photo.fileIdentifier === fileIdentifier ? { ...photo, deleted: 1, deletedAt: new Date() } : photo)));
      } else {
        this.form.controls.photos.value.update(currentMedia => currentMedia.filter(photo => photo.fileIdentifier !== fileIdentifier));
      }
      this.form.get("photos")!.markAsDirty();
      this.form.get("photos")!.updateValueAndValidity({
        onlySelf: true,
        emitEvent: true
      });
      this.form.updateValueAndValidity({
        emitEvent: true
      });

    } catch (error) {
      this.logger.error("Error deleting media", error);
    }
  }

  async deleteIssue(allIssuesDeleted = false): Promise<void> {
    this.isDeletingIssue.set(true);
    await this.bqStepDataService().deleteIssueForAssertion(this.issue());
    this.isDeletingIssue.set(false);

    this.goBack(allIssuesDeleted);
  }

  async confirmDeletion(event?: Event): Promise<void> {
    let message: string = "";

    if (this.from() === "job-final-review") {
      message = "Deleting this response will pause your Final Review. You'll be taken back to re-answer the question.";
    } else {
      message = "<strong>Permanently delete this issue?</strong> This will remove all issues from the question and mark it as not submitted. This action cannot be undone.";
    }

    if (this.isLastIssueOnAssertion()) {
      this.confirmationService.confirm({
        target: event?.target as EventTarget,
        message,
        header: "Delete Issue",
        closable: true,
        closeOnEscape: true,
        rejectButtonProps: {
          label: "Cancel",
          severity: "secondary",
          outlined: false,
          text: true,
        },
        acceptButtonProps: {
          label: "Delete",
          text: true
        },
        accept: async () => {
          this.deleteIssue(true);
        },
        acceptButtonStyleClass: "btn-confirm-cancel",
        rejectButtonStyleClass: "btn-reject-cancel"
      });
    } else {
      this.confirmationService.confirm({
        target: event?.target as EventTarget,
        message: "<strong>Permanently delete this issue?</strong> This action cannot be undone.",
        header: "Delete Issue",
        closable: true,
        closeOnEscape: true,
        rejectButtonProps: {
          label: "Cancel",
          severity: "secondary",
          outlined: false,
          text: true,
        },
        acceptButtonProps: {
          label: "Delete",
          text: true
        },
        accept: async () => {
          this.deleteIssue(false);
        },
        acceptButtonStyleClass: "btn-confirm-cancel",
        rejectButtonStyleClass: "btn-reject-cancel"
      });
    }

  }

  async cancel(event?: Event): Promise<void> {
    if (!!this.form.dirty === false) {
      this.goBack();
      return;
    }

    this.modalService.showModal({
      icon: ModalIcon.None,
      header: "Cancel",
      subHeader: "Are you sure you want to cancel?",
      message: "Any work you've done up to this point will be lost.",
      cancelLabel: "No",
      onCancel: () => { },
      actionLabel: "Yes",
      onAction: () => this.goBack(),
      isDestructive: true
    });
  }

  async removeLooksGoodStatus(event?: Event): Promise<void> {
    let message: string = "";
    let acceptMessage: string = "Yes";
    let cancelMessage: string = "No";

    if (this.from() === "job-final-review") {
      message = "Deleting this response will pause your Final Review. You'll be taken back to re-answer the question.";
      acceptMessage = "Delete";
      cancelMessage = "Cancel";
    } else {
      message = "<strong>Are you sure you want to proceed?</strong><br>This will remove all images & notes from this question and mark it as not submitted. This action cannot be undone.";
    }

    this.confirmationService.confirm({
      target: event?.target as EventTarget,
      message,
      header: "Remove Status",
      closable: true,
      closeOnEscape: true,
      rejectButtonProps: {
        label: cancelMessage,
        severity: "secondary",
        outlined: false,
        text: true,
      },
      acceptButtonProps: {
        label: acceptMessage,
        text: true
      },
      accept: async () => {
        await this.executeRemoveLooksGoodStatus();
      },
      acceptButtonStyleClass: "btn-confirm-cancel",
      rejectButtonStyleClass: "btn-reject-cancel"
    });
  }

  private async executeRemoveLooksGoodStatus(): Promise<void> {
    if (!this.assertion) {
      return;
    }

    try {
      this.isRemovingLooksGoodStatus.set(true);
      await this.bqStepDataService().removeLooksGoodStatus(this.assertion());
      this.isRemovingLooksGoodStatus.set(false);

      await this.goBack();
    } catch (error) {
      this.logger.error("Error removing looks good status", error);
    }
  }

  async goBack(allIssuesDeleted: boolean = false): Promise<void> {
    const areaId = this.area()?.id;
    const componentId = this.component()?.id;
    const assertionId = this.assertion()?.id;

    // --- CASE 1: From Job Issue List ---
    if (this.from() === "job-issue-list") {
      if (allIssuesDeleted) {
        this.router.navigate(["area", areaId], {
          relativeTo: this.route.parent,
          queryParams: { filter: "pending" }
        });
      } else {
        this.router.navigate([
          "area", areaId,
          "component", componentId,
          "assertion", assertionId,
          "issues"
        ], {
          relativeTo: this.route.parent,
          queryParams: { filter: this.originatingFilter() }
        });
      }
      return;
    }

    // --- CASE 2: From Needs Attention Tab ---
    if (this.from() === "needs-attention-tab") {
      this.router.navigate(["overview", "needs-attention"], {
        relativeTo: this.route.parent
      });
      return;
    }

    // --- CASE 3: From Final Review ---
    if (this.from() === "job-final-review") {
      if (this.isIssue()) {
        if (allIssuesDeleted) {
          this.router.navigate(["area", areaId], {
            relativeTo: this.route.parent,
            queryParams: { filter: "pending" }
          });
        } else {
          this.router.navigate(["final-review"], { relativeTo: this.route.parent });
        }
      } else {
        const isAssertionComplete = this.isCcmInspection() ? this.assertion()?.isCcmComplete : this.assertion()?.isComplete;
        if (isAssertionComplete === false) {
          this.router.navigate(["area", areaId], {
            relativeTo: this.route.parent,
            queryParams: { filter: "pending" }
          });
        } else {
          this.router.navigate(["final-review"], {
            relativeTo: this.route.parent,
            queryParams: { step: 2 }
          });
        }
      }
      return;
    }

    // --- DEFAULT CASE: Area navigation ---
    const queryParams = this.activeFilter() ? { filter: this.activeFilter() } : undefined;
    this.router.navigate(["area", areaId], {
      relativeTo: this.route.parent,
      queryParams
    });
  }

  onSeverityClick(level: string) {
    this.form.patchValue({ severity: level });
    this.form.controls.severity.markAsDirty();
  }

  /** Minor / Major / Missing rendered as a single-select horizontal button group. */
  get severityButtons(): HorizontalButton[] {
    const selected = this.form.controls.severity.value;
    return severityLevels.map(level => ({
      label: level.label,
      value: level.value,
      styleClass: selected === level.value
        ? "bqa-severity-btn bqa-severity-btn-selected"
        : "bqa-severity-btn",
      action: () => this.onSeverityClick(level.value),
      testId: `job-needs-attention-btn-severity-${level.value}`
    }));
  }

  get severityInfoMessage(): string {
    switch (this.form.controls.severity.value) {
      case "minor":
        return "A smaller issue typically resolved by a single trade. e.g., Paint touch-ups, scratched sink, door replacement, chipped countertop.";
      case "major":
        return "A significant issue requiring rework and repair involving multiple trade partners. e.g., Uneven outlets in backsplash, damaged appliance, misaligned flooring, bowed wall, improperly installed tile.";
      case "missing":
        return "An essential item that was never installed, such as an appliance, fireplace, or light fixture. Excludes small components like screws or trim caps.";
      default:
        return this.form.controls.severity.value ? "Please select the severity level." : "";
    }
  }

  async saveIssue(mode: "return" | "addAnother" = "return"): Promise<void> {
    const issueId = this.issue().id;
    const issueIdentifier = this.issue().identifier;
    const assertionIdentifier = this.assertion().identifier;
    const jobId = this.job()?.id;

    const notes = this.form.controls.notes.value;
    const severity = this.isCcmInspection() ? this.form.controls.severity.value : unsetSeverity;
    const media = this.form.controls.photos.value;
    const externalVendorId = this.form.controls.externalVendorId?.value ?? null;
    const selectedVendor = externalVendorId ? (this.vendorOptions().find(v => v.id === externalVendorId) ?? null) : null;
    const dueDate = this.form.controls.dueDate?.value ?? null;
    const lotAreaIdentifier = this.isCcmInspection() ? null : this.area().identifier;

    if (issueId) {
      if (this.isLockedAcceptedCarryover()) {
        await this.bqStepDataService().updateIssueSeverityForAssertion(issueId, severity);
      } else {
        await this.bqStepDataService().updateIssueForAssertion(issueId, notes, severity, media(), issueIdentifier, selectedVendor, dueDate, lotAreaIdentifier);
      }
    } else {
      const newIssue: AssertionIssue = {
        text: notes,
        severity,
        isComplete: false,
        isSelfAssigned: false, // Default value for AssertionIssue
        assertionIdentifier,
        identifier: uuidv4(),
        updatedAt: new Date(),
        syncStatus: SyncableStatus.Pending,
        jobId: jobId!,
        createdAt: new Date(),
        isConfirmedByHomeowner: false,
        dueDate: dueDate ?? undefined,
        lotAreaIdentifier: lotAreaIdentifier ?? undefined
      };

      await this.bqStepDataService().createIssueForAssertion(newIssue, media(), selectedVendor);
    }

    if (mode === "addAnother") {
      this.toastService.success("Issue Saved");
      this.resetFormForNewIssue();
      return;
    }

    await this.goBack();
  }

  private resetFormForNewIssue(): void {
    this.issue.set({} as AssertionIssue);
    this.isExistingIssue.set(false);
    this.formSubmitted.set(false);

    const photosSignal = this.form.controls.photos.value;
    photosSignal.set([]);

    this.form.reset({
      notes: "",
      severity: "",
      photos: photosSignal,
      externalVendorId: null,
      dueDate: null
    });
    this.form.markAsPristine();
    this.form.updateValueAndValidity();
    this.allIssuesRelatedToAssertion.set(this.issues().filter(i => i.assertionIdentifier === this.assertion().identifier));
    this.gallery.ref().load(this.galleryItems());
  }

  async saveLooksGoodAssertionDetail(): Promise<void> {
    if (!this.assertion()) {
      return;
    }

    const notes = this.form.controls.notes.value;
    const media = this.form.controls.photos.value;

    await this.bqStepDataService().saveLooksGoodAssertionDetail(this.assertion(), notes, media());

    await this.goBack();
  }

  async save(mode: "return" | "addAnother" = "return"): Promise<void> {
    this.formSubmitted.set(true);

    if (this.form.valid) {
      if (this.form.pristine) {
        await this.goBack();
        return;
      }

      this.isSaving.set(true);
      this.savingMode.set(mode);
      try {
        await this.imageCompressionPipelineService.waitForCompletion();
        if (this.isIssue()) {
          await this.saveIssue(mode);
        } else {
          await this.saveLooksGoodAssertionDetail();
        }
      } finally {
        this.isSaving.set(false);
        this.savingMode.set(null);
      }
    }
  }

  get footerButtons(): VerticalButton[] {
    const buttons: VerticalButton[] = [
      {
        label: this.savingMode() === "return" ? "Saving..." : "Save & Return to List",
        severity: "contrast",
        disabled: this.isSaving(),
        styleClass: "bqa-primary-btn submit-button",
        action: () => this.save("return"),
        testId: "job-needs-attention-btn-save"
      }
    ];

    if (this.isIssue() && !this.isExistingIssue()) {
      buttons.push({
        label: this.savingMode() === "addAnother" ? "Saving..." : "Save & Add Another Issue",
        severity: "contrast",
        outlined: true,
        disabled: this.isSaving(),
        styleClass: "bqa-primary-btn",
        action: () => this.save("addAnother"),
        testId: "job-needs-attention-btn-save-add-another"
      });
    }

    if (this.issue().id && this.isIssue()) {
      buttons.push({
        label: this.isDeletingIssue() ? "Deleting..." : "Delete Issue",
        severity: "danger",
        outlined: true,
        disabled: this.isDeletingIssue(),
        styleClass: "bqa-danger-btn",
        action: () => this.confirmDeletion(),
        testId: "job-needs-attention-btn-delete"
      });
    }

    if (this.isLooksGoodAndComplete() && !this.isIssue()) {
      buttons.push({
        label: this.isRemovingLooksGoodStatus() ? "Removing..." : "Remove Looks Good Status",
        severity: "contrast",
        outlined: true,
        disabled: this.isRemovingLooksGoodStatus(),
        styleClass: "bqa-secondary-btn",
        action: () => this.removeLooksGoodStatus(),
        testId: "job-needs-attention-btn-remove-looks-good"
      });
    }

    buttons.push({
      label: "Cancel",
      severity: "secondary",
      text: true,
      styleClass: "bqa-secondary-btn cancel-button",
      action: () => this.cancel(),
      testId: "job-needs-attention-btn-cancel"
    });

    return buttons;
  }

  // This allows to upload multiple files when clicking in "Library"
  async onPhotosSelected(files: File[], insertIndex?: number): Promise<Media[]> {
    if (files.length === 0 || this.isLockedAcceptedCarryover()) {
      return [];
    }

    const looksGoodImageType = this.isCcmInspection() ? ImageType.LooksGoodCustomerCareManager : ImageType.LooksGoodConstructionManager;
    const imageType = this.isIssue() ? ImageType.Issue : looksGoodImageType;
    const mediaRecords = await this.imageCompressionPipelineService.encode(files, (this.itemIdentifier()), imageType);

    this.form.controls.photos.value.update(currentMedia => insertAt(currentMedia, mediaRecords, insertIndex));

    this.imageCompressionPipelineService.compress(mediaRecords, async compressedMediaRecords => {
      this.form.controls.photos.value.update(currentMedia => {
        return currentMedia.map(originalImage => {
          const compressedImage = compressedMediaRecords.find(m => m.fileIdentifier === originalImage.fileIdentifier);
          if (compressedImage) {
            const updatedRecord: Media = {
              ...originalImage,
              base64Image: compressedImage.base64Image,
              size: compressedImage.size,
              mimeType: compressedImage.mimeType
            };
            return updatedRecord;
          }
          return originalImage;
        });
      });

      this.form.get("photos")!.markAsDirty();
      this.form.get("photos")!.updateValueAndValidity({
        onlySelf: true,
        emitEvent: true
      });
      this.form.updateValueAndValidity({
        emitEvent: true
      });
    });

    return mediaRecords;
  }


  isInvalid(controlName: string): boolean {
    const control = this.form.get(controlName);
    return !!(control?.invalid && (control.touched || this.formSubmitted()));
  }

  hasMediaValidator(control: AbstractControl): ValidationErrors | null {
    const hasMedia = control.value()?.filter((x: Media) => x.deleted === 0).length > 0;
    return hasMedia ? null : { required: true };
  };

  onCalendarShow(): void {
    this.isCalendarOpen.set(true);

    if (this.backdropTimeout()) {
      clearTimeout(this.backdropTimeout()!);
    }
  }

  onCalendarHide(): void {
    const timeout = setTimeout(() => {
      this.isCalendarOpen.set(false);
    }, 100);
    this.backdropTimeout.set(timeout);
  }

  private configureLockedCarryoverFormIfNeeded(): void {
    if (!this.isLockedAcceptedCarryover()) {
      return;
    }

    this.form.controls.notes.clearValidators();
    this.form.controls.notes.setValidators(Validators.maxLength(this.maxNoteLength));
    this.form.controls.notes.disable();
    this.form.controls.photos.clearValidators();
    this.form.controls.notes.updateValueAndValidity();
    this.form.controls.photos.updateValueAndValidity();
  }
}
